<template>
    <div class="tw-mb-12">
        <div class="tw-pb-4 input-group" :class="{ 'd-none': width < 992 && (chatActive || startChat) }">
            <input id="msg-friends"
                v-model="filterConversations"
                type="text"
                class="form-control"
                placeholder="Search by email, name or phone"
                @keypress.13="fetchConversations">

            <div class="input-group-append">
                <span class="input-group-text">
                    <i class="fa fa-search"></i>
                </span>
            </div>
        </div>

        <card-spinner v-if="loading" style="height: 200px;"/>

        <template v-else>
            <div class="lg:tw-flex tw-pb-8 h-screen">
                <div class="block__conversations lg:tw-w-1/3 xl:tw-w-1/4 lg:tw-pr-3"
                    :class="{ 'd-none': !showConversations && !startChat }">

                    <div v-if="width < 992 && startChat" class="tw-bg-white tw-mb-4 tw-px-4 tw-py-3 tw-rounded">
                        <div @click="backStartChat()">
                            <i class="fas fa-chevron-left tw-mr-1"></i> Back
                        </div>
                    </div>

                    <div class="tw-mb-6 hover:tw-bg-grey-lightest tw-cursor-pointer tw-bg-white tw-dark tw-flex tw-p-4 tw-rounded"
                        :class="{'tw-bg-blue-dark hover:tw-bg-blue-dark tw-text-white' : startChat}"
                        @click="startNewChat()">
                        <i class="fa fa-comments tw-text-2xl tw-mr-3" :class="{'tw-text-blue-dark': !startChat}"></i>
                        <div>Start chat</div>
                    </div>

                    <!-- Filters open/closed -->
                    <div class="tw-mb-6" :class="{ 'd-none': width < 992 && startChat }">
                        <div class="btn-group w-100" role="group" aria-label="Conversations filters">
                            <button type="button"
                                v-for="(item, index) in filters"
                                :key="index"
                                :class="[
                                    'btn',
                                    'shadow-none',
                                    (item === filterSelected) ? 'btn-primary' : 'btn-default'
                                ]"
                                @click="filterSelected = item">
                                {{ item | btnFilter }}
                            </button>
                        </div>
                    </div>

                    <template v-if="showConversations">
                        <div class="tw-overflow-y-auto">
                            <template v-if="loadingConversations">
                                <div class="tw-dark tw-bg-white tw-mb-4 tw-p-4 tw-rounded" v-for="line in 6" :key="line">
                                    <div class="text__line" v-for="textLine in 4" :key="textLine"></div>
                                </div>
                            </template>
                            <div v-else v-for="(conversation, index) in conversations" :key="`conversation-${index}`"
                                class="tw-relative tw-dark tw-bg-white tw-mb-4 tw-rounded">
                                <div class="tw-cursor-pointer tw-p-4 tw-rounded"
                                    :class="[{
                                        'tw-bg-blue-dark hover:tw-bg-blue-dark' : conversation.active,
                                    }, conversation.contact.unsubscribed
                                        ? 'tw-bg-orange-lightest hover:tw-bg-orange-lighter'
                                        : 'hover:tw-bg-grey-lightest'
                                    ]"
                                    @click="loadChat(conversation)">
                                    <div :class="{'tw-font-bold':conversation.unread, 'tw-font-light': !conversation.unread}">
                                        <div class="tw-flex tw-items-center tw-pr-6">
                                            <div class="tw-truncate tw-w-full" :class="{'tw-text-white' : conversation.active}">
                                                {{ conversation | getTitle }}
                                            </div>
                                            <div class="date__message tw-pin-r tw-pin-t tw-text-grey-dark tw-text-xs tw-ml-2"
                                                :class="{'tw-text-blue-lighter' : conversation.active}">
                                                {{ conversation.dateMessage | humanize(timezone) }}
                                            </div>
                                        </div>
                                        <div class="tw-flex tw-items-center tw-pt-3 tw-text-sm">
                                            <p class="tw-truncate tw-w-full tw-relative"
                                                :class="{'tw-text-white' : conversation.active}">
                                                {{ conversation.lastMessage }}
                                            </p>

                                            <span class="tw-ml-1 tw-w-5 tw-h-5 tw-bg-red tw-rounded-full tw-text-white
                                                tw-flex tw-items-center tw-justify-center tw-content-center"
                                                v-if="conversation.unread">
                                                {{ countMessageUnread(index) }}
                                            </span>
                                        </div>
                                    </div>
                                    <p class="tw-mt-2 received__from" style="color: #2CBF2C"
                                        :class="{'chatActive': conversation.active}"
                                        v-if="isWebChat(index)">
                                        Received from Web Chat
                                    </p>
                                    <p class="tw-mt-2 received__from" style="color: #2CBF2C"
                                        :class="{'chatActive': conversation.active}"
                                        v-if="conversation.contact.source === 'facebook'">
                                        Received from Facebook Page
                                    </p>
                                </div>
                                <div class="dropdown tw-absolute tw-pin-r dropdown__actions">
                                    <button class="tw-text-grey-dark tw-py-1 tw-px-4 tw-relative tw-z-10"
                                        type="button" id="dropdownConversation"
                                        data-toggle="dropdown" aria-haspopup="true"
                                        aria-expanded="false">
                                        <i class="fas fa-ellipsis-v"></i>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right"
                                        aria-labelledby="dropdownConversation">
                                        <a class="dropdown-item"
                                            @click="deleteConversation(conversation.contact.id)">
                                            Delete
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <pagination
                            :spacing="1"
                            :meta="pagination.meta"
                            :links="pagination.links"
                            @page="fetchConversations"
                        />
                    </template>
                </div>

                <div v-if="chatActive && !startChat" class="block__conversation lg:tw-w-2/3 xl:tw-w-3/4 xl:tw-flex">
                    <div class="xl:tw-w-2/3 xl:tw-pr-3 h-100" v-if="showConversation">
                        <div class="tw-flex align-items-center justify-content-between tw-bg-white tw-mb-4 tw-px-4
                            tw-py-3 tw-rounded d-lg-none">
                            <div @click="backConversations()">
                                <i class="fas fa-chevron-left tw-mr-1"></i> Back
                            </div>

                            <!-- Open / Close -->
                            <div>
                                <div class="tw-flex tw-items-center switch" v-if="chatActive.messages.length">
                                    <div @click="statusConversation('Open')">Open</div>

                                    <div class="mx-2" @click="statusConversation()" style="font-size: 22px;">
                                        <i class="mt-1 fa fa-lg fa-toggle-on fa-rotate-180 text-primary" v-if="toggled"></i>
                                        <i class="mt-1 fa fa-lg fa-toggle-on text-muted" v-if="!toggled"></i>
                                    </div>

                                    <div @click="statusConversation('Close')">Close</div>
                                </div>

                                <div id="messageChangeStatus" v-show="newStatus">
                                    Message {{ newStatus }}!
                                </div>
                            </div>
                        </div>

                        <div class="h-100 lg:tw-flex lg:tw-flex-col lg:tw-justify-between"
                            :class="{ 'd-none': !chatActive }">
                            <div class="tw-bg-white tw-flex align-items-center tw-justify-between tw-mb-4 tw-p-4
                                tw-rounded">
                                <div class="tw-text-lg" @click="showContactInfo()">
                                    {{ chatActive.name }}
                                    <div v-if="contact" class="tw-font-light tw-text-sm">
                                        <template v-if="contact.source === 'facebook'">
                                            Contact by Facebook Page
                                        </template>
                                        <template v-else>
                                            {{ contact.phone_number }}
                                            <span v-if="contact.phone_number && contact.email"> | </span>
                                            {{ contact.email }}
                                        </template>
                                    </div>
                                </div>

                                <!-- Open / Close -->
                                <div class="d-none d-lg-block">
                                    <div class="tw-flex tw-items-center switch" v-if="chatActive.messages.length">
                                        <div @click="statusConversation('Open')">Open</div>

                                        <div class="mx-2" @click="statusConversation()" style="font-size: 22px;">
                                            <i class="mt-1 fa fa-lg fa-toggle-on fa-rotate-180 text-primary" v-if="toggled"></i>
                                            <i class="mt-1 fa fa-lg fa-toggle-on text-muted" v-if="!toggled"></i>
                                        </div>

                                        <div @click="statusConversation('Close')">Close</div>
                                    </div>

                                    <div id="messageChangeStatus" v-show="newStatus">
                                        Message {{ newStatus }}!
                                    </div>
                                </div>
                            </div>

                            <div id="msgHistory" class="tw-bg-white tw-p-4 tw-rounded tw-overflow-y-auto">
                                <div v-for="(item, index) in messages" :key="`messages-${index}`" class="sm:tw-flex"
                                    :class="{'sm:tw-justify-end' : item.direction === 'send'}"
                                    @change="scrollEndPage()">
                                    <div :class="{
                                        'tw-pl-8 sm:tw-pl-32' : item.direction === 'send',
                                        'tw-pr-8 sm:tw-pr-32' : item.direction !== 'send'
                                    }">
                                        <div class="tw-p-3 tw-rounded-xl"
                                            v-if="item.media_type === 'text'"
                                            :class="{
                                                'tw-bg-blue-lighter tw-text-blue-dark' : item.direction === 'send',
                                                'tw-bg-grey-lighter tw-text-grey-dark' : item.direction !== 'send'
                                            }"
                                        >
                                            {{ item.text }}

                                            <span v-if="item.web_chat_url_origin">
                                                <br>
                                                <small>
                                                    This message was received from
                                                    <a style="color: blue; " v-bind:href="item.web_chat_url_origin" >
                                                        <u>{{ item.web_chat_url_origin }}.</u>
                                                    </a>
                                                </small>
                                            </span>
                                        </div>

                                        <div class="tw-py-3"
                                            v-if="item.media_type === 'audio'">
                                            <audio controls>
                                                <source :src="item.text">
                                            </audio>
                                        </div>

                                        <div v-if="item.media_type === 'image'"
                                            class="tw-p-3 tw-rounded-xl"
                                             :class="{
                                                'tw-bg-blue-lighter tw-text-blue-dark' : item.direction === 'send',
                                                'tw-bg-grey-lighter tw-text-grey-dark' : item.direction !== 'send'
                                            }">
                                            <img :src="item.text" @click="imageSelected(item.text)" class="tw-rounded-xl">
                                        </div>

                                        <div class="tw-p-3 tw-rounded-xl"
                                             :class="{
                                                'tw-bg-blue-lighter tw-text-blue-dark' : item.direction === 'send',
                                                'tw-bg-grey-lighter tw-text-grey-dark' : item.direction !== 'send'
                                            }"
                                            v-if="item.media_type === 'video'">
                                            <video controls class="tw-rounded-xl tw-outline-none" width="100%" height="100%">
                                                <source :src="item.text">
                                            </video>
                                        </div>
                                        <div class="tw-p-3 tw-rounded-xl"
                                             :class="{
                                                'tw-bg-blue-lighter tw-text-blue-dark' : item.direction === 'send',
                                                'tw-bg-grey-lighter tw-text-grey-dark' : item.direction !== 'send'
                                            }"
                                             v-if="item.media_type === 'pdf'">
                                            <a :href="item.text">View/Download </a>
                                            <embed :src="item.text" width="100%" height="100%" />
                                        </div>
                                        <div class="tw-text-xs tw-mb-2 tw-mt-1 tw-text-grey-dark"
                                            :class="{
                                                'tw-text-left tw-mr-2': item.direction !== 'send',
                                                'tw-text-right tw-ml-2': item.direction === 'send',
                                            }">
                                            <span class="mr-4" v-if="item.origin === 'webchat'">
                                                <img :src="`${base_url}/img/webchat-name-icon.svg`">
                                                By Web Chat: {{ item.web_chat_name }}
                                            </span>
                                            <span class="mr-4" v-if="!item.direction && item.origin === 'facebook'">
                                                <i class="fab fa-facebook-messenger"></i>
                                                By Facebook Page
                                            </span>
                                            <span>{{ item.date_sent | calendar(timezone) }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <template-select
                                :contact="chatActive.contact"
                                @changed="selectedTemplate"
                            />

                            <div v-if="!contact.unsubscribed" class="tw-bg-white tw-flex align-items-center tw-mt-4 tw-p-5 tw-rounded">
                                <div v-if="textFileUpload">
                                    <i @click="textFileUpload = null" class="fa fa-window-close pull-right"></i>
                                    <img v-if="fileUploadType === 'image'" height="100" width="100" :src="imageUrl">
                                    <div class="pr-2" v-if="fileUploadType === 'pdf'">
                                        <i class="fas fa-file-pdf fa-2x"></i>
                                    </div>
                                    <div class="pr-2" v-if="fileUploadType === 'video'">
                                        <i class="fas fa-video fa-2x"></i>
                                    </div>
                                </div>
                                <textarea
                                ref="textMsg"
                                v-model="textMsg"
                                class="tw-min-h-0 tw-mr-4 tw-outline-none tw-pt-1 tw-resize-none
                                tw-text-grey-darker tw-text-lg tw-w-full tw-leading-none"
                                placeholder="Type a message"
                                :style="{
                                    height: textarea.height + 'px',
                                    maxHeight: textarea.maxHeight + 'px',
                                    lineHeight: textarea.lineHeight + 'px'
                                }"
                                @keyup.shift.enter="sendMsg()"
                                :disabled="sending"
                                ></textarea>
                                <button type="button" class="btn btn-primary tw-uppercase"
                                    @click="sendMsg()" style="height: 42px" :disabled="sending">
                                    <i class="fa fa-spin fa-spinner" v-if="sending"></i>
                                    <span v-else>send</span>
                                </button>
                            </div>
                            <div v-if="!contact.unsubscribed" class="messenger-bg tw-mt-0 tw-p-2 tw-rounded">
                                <div class="row">
                                    <div class="col-md-3">
                                        <button type="button" class="btn btn-default tw-uppercase upload-btn-wrapper"
                                                style="height: 35px" :disabled="sending">
                                            <i class="fa fa-image"></i>
                                            <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/pdf,application/pdf,video/*" @change="upload"/>
                                        </button>
                                        &nbsp
                                        <button @click="toggleEmojiList" type="button" class="btn btn-default tw-uppercase"
                                                style="height: 35px" :disabled="sending">
                                            <i class="far fa-grin"></i>
                                        </button>
                                    </div>
                                    <div class="col-md-7">
                                        <div v-show="showEmjoiList" class="messenger-bg tw-p-2 tw-rounded">
                                            <VEmojiPicker style="background-color: #ffffff" emojisByRow="7" @select="selectEmoji" />
                                        </div>
                                    </div>
                                    <div class="col-md-2 ">
                                        &nbsp
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Contact Info -->
                    <div class="xl:tw-w-1/3 contact__info" v-if="chatActive.contact && showDetailsContact">
                        <div class="tw-bg-white tw-mb-4 tw-px-4 tw-py-3 tw-rounded d-xl-none">
                            <div @click="backDetailsContact()">
                                <i class="fas fa-chevron-left tw-mr-1"></i> Back
                            </div>
                        </div>
                        <ContactInfo
                            class="h-100"
                            :chat-active="chatActive"
                        />
                    </div>
                </div>

                <ContactList
                    :class="{'d-none': !startChat}"
                    ref="contactList"
                    @startChat="startChatWith"
                />
            </div>
        </template>
        <modal ref="modal"
               size="lg"
               @hide="close()"
               :scrollable="true"
               :has-footer="false"
               :centered="true">
            <template slot="title">Image Preview</template>
            <div class="text-center">
                <img :src="selectedImageUrl" @click="openImageInTab">
            </div>
        </modal>
    </div>
</template>

<script>
    import _ from 'lodash';
    import Form from '../../utils/Form';
    import TemplateSelect from './TemplateSelect';
    import ContactList from './ContactList';
    import ContactInfo from './ContactInfo';
    import VEmojiPicker from 'v-emoji-picker';

    export default {
        components: { TemplateSelect, ContactList, ContactInfo, VEmojiPicker },

        filters: {
            calendar(value, timezone) {
                let date;
                if (!timezone){
                    timezone = moment.tz.guess();
                }
                if (_.has(value, 'date')) {
                    date = moment.tz(value.date, 'UTC').tz(timezone);
                }

                if (moment(value).isValid()) {
                    date = moment.tz(value, 'UTC').tz(timezone);
                }

                if (date) {
                    if (
                        !date.isAfter(
                            moment()
                                .subtract(7, 'days')
                                .startOf('day')
                        )
                    ) {
                        return date.format('llll');
                    }

                    return date.calendar();
                }

                return value;
            },

            humanize(value, timezone) {
                if (!timezone){
                    timezone = moment.tz.guess();
                }
                if (_.isObject(value)) {
                    return moment
                        .tz(value.date, 'UTC')
                        .tz(timezone)
                        .fromNow();
                }

                if (moment(value).isValid()) {
                    return moment
                        .tz(value, 'UTC')
                        .tz(timezone)
                        .fromNow();
                }

                return value;
            },

            btnFilter(value) {
                if (value == 'Close') value += 'd';

                return value;
            },

            getTitle(conversation) {
                let name = conversation.name;
                let phone = conversation.to;

                if (!name) return phone;

                return name;
            }
        },

        data() {
            return {
                timezone: null,
                startChat: false,
                loading: true,
                loadingConversations: false,
                messages: [],
                textMsg: '',
                conversations: [],
                pagination: {},
                chatActive: null,
                filterConversations: '',
                sending: false,
                filters: [],
                filterNewMessage: null,
                filterSelected: 'Open',
                newStatus: null,
                width: null,
                showDetailsContact: false,
                showConversations: true,
                showConversation: false,
                base_url: window.location.origin,
                textarea: {
                    height: 32,
                    maxHeight: 100,
                    lineHeight: 22
                },
                showEmjoiList: false,
                textFileUpload: null,
                fileUploadType: 'image',
                form: new Form ({file: null}, 'multipart'),
                selectedImageUrl: ''
            };
        },

        computed: {
            contact() {
                return this.chatActive ? this.chatActive.contact : null;
            },

            toggled() {
                return this.messages[this.messages.length - 1].status === 'Open';
            },

            imageUrl() {
                if (typeof (this.form.file) === 'string') {
                    return this.url || this.form.file;
                }
                return URL.createObjectURL(this.form.file);
            }
        },

        updated() {
            this.scrollEndPage();
        },

        watch: {
            filterSelected() {
                if (this.filterSelected === 'Close' && this.startChat) {
                    this.startChat = false;
                }
                this.fetchConversations();
            },

            width() {
                this.reponsiveMode();
            },

            textMsg() {
                let height = this.$refs['textMsg'].scrollHeight;
                let temp = document.querySelector('.temp__width__textarea');

                if (temp) {
                    let height = temp.offsetHeight;
                    temp.parentNode.removeChild(temp);

                    return (this.textarea.height = height);
                }

                if (!this.textMsg.length) return (this.textarea.height = 30);

                this.textarea.height = height;
            }
        },

        async created() {
            window.addEventListener('resize', this.onResize);
            this.onResize();
            await this.fetchTimezone();
            await this.getFilters();
            await this.fetchConversations().then(() => {
                this.loading = false;
            });

            const teamId = this.$root.currentTeam.id;

            window.Echo.private(`App.Team.${teamId}`)
                .listen('SmsSent', ({ message }) => this.onSmsSent(message))
                .listen('.facebook.received', ({ message }) => this.onMessageReceived(message))
                .listen('.facebook.sent', ({ message }) => this.onMessageReceived(message))
                .listen('.sms.received', ({ sms }) => {

                    let conversation = _.first(this.conversations.filter(i => i.contact.id === sms.contact.id));

                    if (!conversation) {
                        conversation = this.createObjectToChat(sms.contact, false);
                        this.conversations.unshift(conversation);
                        if (this.conversations.length > 6) this.conversations.pop();
                    }

                    let isUnread = this.isUnread(sms.contact, this.$root.user.id);

                    let message = {
                        id: sms.id,
                        direction: 'received',
                        text: sms.body,
                        date: new Date(),
                        status: sms.status,
                        read: sms.read,
                        unread: isUnread,
                        origin: sms.origin || '',
                        web_chat_name: sms.web_chat_name || '',
                        media_type: sms.media_type,
                    };

                    conversation.messages.push(message);
                    conversation.lastMessage = message.text;
                    conversation.dateMessage = message.date;
                    conversation.unread = isUnread;

                    if (sms.contact.id === this.chatActive.contact.id) {
                        this.chatActive.unread = isUnread;
                        this.messages.push(message);
                    }

                    this.markAsRead();
                });
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.onResize);
        },

        methods: {
            imageSelected(imageUrl) {
                this.selectedImageUrl = imageUrl;
                this.$refs.modal.open();
            },
            openImageInTab() {
                window.open(this.selectedImageUrl, '_blank');
            },
            async fetchTimezone()  {
                return this.$http.get(this.$laroute.route('api.teams.current-team-timezone')).then(({ data }) => {
                    this.timezone = data;
                });
            },

            resetImgUploader() {
                const input = this.$refs.fileInput;
                input.type = 'text';
                input.type = 'file';
            },
            upload(event) {
                this.form.file = event.target.files[0];
                let fileType = event.target.files[0].type;
                if(fileType === 'video/quicktime' || fileType === 'video/mp4') {
                    this.fileUploadType = 'video';
                } else if(fileType === 'application/pdf'){
                    this.fileUploadType = 'pdf';
                }else {
                    this.fileUploadType = 'image';
                }
                this.uploadServer();
            },

            uploadServer() {
                this.form.post(this.$laroute.route('api.files.temp-' + this.fileUploadType))
                    .then(this.onUploadSuccess.bind(this))
                    .catch(this.onUploadFailed.bind(this));
            },

            onUploadSuccess({data}) {
                this.textFileUpload = data.path;
                this.resetImgUploader();
            },

            onUploadFailed(response) {
                console.error('response - Error', response);
                this.textFileUpload = null;
                this.resetImgUploader();
            },
            toggleEmojiList() {
                this.showEmjoiList = !this.showEmjoiList;
            },

            selectEmoji(emoji) {
                this.textMsg = this.textMsg + emoji.data;
                this.showEmjoiList = false;
            },

            onResize() {
                this.width = window.innerWidth;
            },

            onSmsSent(sms) {
                let conversation = _.first(this.conversations.filter(i => i.contact.id === sms.contact.id));

                if (!conversation) {
                    conversation = this.createObjectToChat(sms.contact, false);
                    this.conversations.unshift(conversation);
                    if (this.conversations.length > 6) this.conversations.pop();
                }

                if (conversation.messages.find(m => m.id === sms.id)) {
                    return;
                }

                let message = {
                    ...sms,
                    direction: 'send',
                    date: new Date(sms.date_sent),
                    origin: sms.origin || '',
                    web_chat_name: sms.web_chat_name || ''
                };

                conversation.messages.push(message);
                conversation.lastMessage = message.text;
                conversation.dateMessage = message.date;
                conversation.unread = false;

                if (sms.contact.id === this.chatActive.contact.id) {
                    this.messages.push(message);
                }
            },

            onMessageReceived(msg) {
                let conversation = _.first(this.conversations.filter(i => i.contact.id === msg.contact.id));

                if (!conversation) {
                    conversation = this.createObjectToChat(msg.contact, false);
                    this.conversations.unshift(conversation);
                    if (this.conversations.length > 6) this.conversations.pop();
                }

                let message = {
                    ...msg,
                    direction: msg.direction,
                    text: msg.body,
                    date: new Date(),
                    status: msg.status,
                    read: msg.read,
                    web_chat_name: msg.web_chat_name || ''
                };

                conversation.messages.push(message);
                conversation.lastMessage = message.text;
                conversation.last_message_web_chat_url_origin = data.web_chat_url_origin;
                conversation.dateMessage = message.date;
                conversation.origin      = message.origin;
                conversation.unread      = true;
                conversation.facebook_page_id = msg.facebook_page_id;

                if (msg.contact.id === this.chatActive.contact.id) {
                    this.chatActive.unread = true;
                    this.messages.push(message);
                    this.markAsRead();
                }
            },

            getInitials(value) {
                const splittedValue = value.trim().split(' ');
                return splittedValue.length === 1 ? splittedValue[0][0] + splittedValue[0][1] : splittedValue[0][0] + splittedValue[1][0];
            },

            selectedTemplate(value) {
                let widthTextarea = this.$refs['textMsg'].scrollWidth;
                let elem = document.createElement('div');
                elem.classList.add('temp__width__textarea');
                elem.setAttribute(
                    'style',
                    `width: ${widthTextarea}px; line-height: ${
                        this.textarea.lineHeight
                    }px; font-size: 1.125rem; padding-top: .25rem; border: 1px solid; position:relative; left: -9999px;`
                );
                elem.textContent = '';
                elem.textContent = value;
                document.body.appendChild(elem);
                /* End create temporary element */

                this.textMsg = value;
                this.$refs['textMsg'].focus();
            },

            startNewChat() {
                this.chatActive = null;
                this.startChat = true;
                this.conversations.forEach(i => (i.active = false));
                this.$refs.contactList.getContact();

                if (this.width < 992) {
                    this.$store.commit('activeChatMessenger', true);
                    this.showConversations = false;
                }

                if (this.filterSelected === 'Close') {
                    this.filterSelected = 'Open';
                }
            },

            startChatWith(contact) {
                const conversation = _.first(this.conversations.filter(i => i.contact && i.contact.id === contact.id));

                if (conversation) {
                    this.loadChat(conversation);
                    return;
                }

                this.fetchConversation(contact);

                if (this.width < 992) {
                    this.showConversation = true;
                    this.$store.commit('activeChatMessenger', true);
                }
            },

            createObjectToChat(contact, active) {
                return {
                    active: active,
                    contact,
                    initials: contact.name ? this.getInitials(contact.name) : this.getInitials(contact.phone_number),
                    lastMessage: null,
                    messages: [],
                    name: contact.name,
                    to: contact.phone_number
                };
            },

            isUnread(contact, userId) {
                return !contact.assigned_users || contact.assigned_users.length == 0 || contact.assigned_users.includes(userId);
            },

            async fetchConversations(page = 1, load = true) {
                this.loadingConversations = true;

                const params = {
                    page,
                    search: this.filterConversations,
                    status: this.filterSelected
                };

                let { data } = await this.$http.get(this.$laroute.route('api.chat.conversations'), { params });
                this.conversations = data.data;
                this.pagination = data;
                this.loadingConversations = false;

                // Responsive Mode
                if (this.width < 991) return;

                if (load && !this.startChat) {
                    this.conversations.length ? this.loadChat(this.conversations[0]) : setTimeout(() => this.startNewChat(), 200);
                    return;
                }

                if (this.startChat) return;

                let conversations = this.conversations.filter(elem => elem.contact.id === this.chatActive.contact.id);

                if (conversations.length) {
                    this.loadChat(conversations[0]);
                }
            },

            async fetchConversation(contact) {
                await this.$http
                    .get(
                        this.$laroute.route('api.chat.conversation', {
                            contact: contact.id
                        })
                    )
                    .then(({ data }) => {
                        let conversation = {
                            active: true,
                            contact: {
                                id: contact.id,
                                name: contact.name,
                                email: contact.email,
                                groups: contact.groups,
                                locations: contact.locations,
                                phone_number: contact.phone_number,
                                reviews: contact.reviews
                            },
                            initials: contact.initial,
                            name: contact.name,
                            to: contact.phone_number,
                            messages: []
                        };

                        if (data !== '') {
                            (conversation.dateMessage = data.date_sent),
                                (conversation.lastMessage = data.text),
                                (conversation.last_message_web_chat_url_origin = data.web_chat_url_origin),
                                (conversation.unread = !data.read),
                                (conversation.messages = data.twilio_messages);
                        }

                        this.conversations.unshift(conversation);
                        this.loadChat(conversation);
                        this.cleanForm();
                    });
            },

            loadChat(conversation) {
                if (this.width >= 992 && this.width <= 1199 && this.showDetailsContact) {
                    this.showConversation = true;
                    this.showDetailsContact = false;
                }

                if (this.width < 992) {
                    this.showConversations = false;
                    this.showConversation = true;
                    this.$store.commit('activeChatMessenger', true);
                }

                this.changeNewStatusConversation();
                this.messages = _.sortBy(conversation.messages, [
                    function(item) {
                        return item.dateSort;
                    }
                ]);
                this.conversations.forEach(i => (i.active = false));
                conversation.active = true;
                this.startChat = false;
                this.chatActive = conversation;
                this.markAsRead();
                this.scrollEndPage();
            },

            markAsRead() {
                if (this.chatActive.unread) {
                    let contact_id = this.chatActive.contact.id;

                    this.$http
                        .put(
                            this.$laroute.route('api.chat.read-message', {
                                contact: contact_id
                            })
                        )
                        .then(() => {
                            let conversation = this.conversations.find(item => item.contact.id === contact_id);
                            conversation.unread = false;

                            conversation.messages.forEach(function(message) {
                                message.read = true;
                            });

                            Bus.$emit('messengerNotifications');
                        });
                }
            },

            async sendMsg() {
                if (this.textMsg.length <= 0 && !this.textFileUpload) {
                    return;
                }
                let textAndImage = false;
                if (this.textMsg.length > 0 && this.textFileUpload){
                    textAndImage = true;
                }
                this.sending = true;
                let idMessage = null;
                let conversation = this.getFirstConversation();
                let mediaTypeMessage = 'text';
                let textMessage = this.textMsg;
                let imageIdMessage = null;
                let imageText = this.textFileUpload;
                try {
                    const form = new Form({
                        to: this.chatActive.contact.phone_number,
                        text: this.textMsg,
                        status: 'Open',
                        origin: this.chatActive.contact.source,
                        contact_id: this.chatActive.contact.id,
                        facebook_page_id: this.chatActive.facebook_page_id,
                        imgUrl: this.textFileUpload,
                        fileType: this.fileUploadType,
                    });

                    await form.post(this.$laroute.route('api.chat.sendMessage')).then(({ data }) => {
                        idMessage = data.id;
                        if (this.textFileUpload && !textAndImage){
                            mediaTypeMessage = data.media_type;
                            textMessage = data.text;
                            this.textFileUpload = null;
                        }
                        if (textAndImage) {
                            imageIdMessage = data.imgId;
                            imageText = data.imgText;
                            this.textFileUpload = null;
                        }
                    });
                } catch (e) {
                    this.$alert({
                        message:
                            "Something went wrong... \n\nWe're sorry that happened. Our development team" +
                            'already received the details of the error and they will be working to fix this issue in' +
                            'high priority.',
                        type: 'error',
                        title: 'Oops!'
                    });

                    return;
                } finally {
                    this.sending = false;
                }
                if (textAndImage) {
                    let imgMessage = {
                        id: imageIdMessage,
                        direction: 'send',
                        text: imageText,
                        date: new Date(),
                        status: 'Open',
                        media_type: this.fileUploadType
                    };

                    conversation.messages.push(imgMessage);
                    conversation.lastMessage = imgMessage.text;
                    this.messages.push(imgMessage);
                }
                let message = {
                    id: idMessage,
                    direction: 'send',
                    text: textMessage,
                    date: new Date(),
                    status: 'Open',
                    media_type: mediaTypeMessage
                };

                conversation.messages.push(message);
                conversation.lastMessage = message.text;
                this.messages.push(message);

                this.cleanForm();
                this.scrollEndPage();
            },

            cleanForm() {
                this.textMsg = '';
            },

            scrollEndPage(page = 'msgHistory') {
                const chat = this.$el.querySelector(`#${page}`);
                if (chat) {
                    chat.scrollTop = chat.scrollHeight;
                }
            },

            async getFilters() {
                await this.$http.get(this.$laroute.route('api.chat.status')).then(({ data }) => {
                    this.filters = data;
                });

                this.filterNewMessage = this.filters[0];
            },

            async statusConversation(value) {
                if (value === undefined && this.messages[this.messages.length - 1].status === 'Open') {
                    value = 'Close';
                } else if (value === undefined && this.messages[this.messages.length - 1].status === 'Close') {
                    value = 'Open';
                }

                this.messages[this.messages.length - 1].status = value;
                await this.changeStatusConversation();
                await this.changeNewStatusConversation(value);

                this.fetchConversations(this.pagination.meta.current_page, false);
            },

            async changeStatusConversation() {
                let ids = [];

                this.messages.forEach(function(item) {
                    ids.push(item.id);
                });

                const payload = {
                    newStatus: this.messages[this.messages.length - 1].status,
                    idsMessages: ids
                };

                await this.$http.put(this.$laroute.route('api.chat.new-status', payload));
            },

            async changeNewStatusConversation(newValue = null) {
                if ('Open' === newValue) newValue += 'ed';

                if ('Close' === newValue) newValue += 'd';

                this.newStatus = newValue;
            },

            countMessageUnread(value) {
                return this.conversations[value].messages.filter(message => !message.read).length;
            },

            isWebChat(index) {
                return this.conversations[index].messages.find(message => message.origin === 'webchat');
            },

            getFirstConversation() {
                let firstConversation = _.first(this.conversations.filter(c => c.contact.id === this.chatActive.contact.id));

                if (typeof firstConversation === 'undefined') {
                    firstConversation = this.createObjectToChat(this.contact, true);
                }

                return firstConversation;
            },

            deleteConversation(contact_id) {
                this.$confirm().then(() => {
                    this.$http
                        .delete(
                            this.$laroute.route('api.chat.conversation.destroy', {
                                contact: contact_id
                            })
                        )
                        .then(() => {
                            this.fetchConversations(this.pagination.meta.current_page);
                            this.$alert({
                                message: 'Conversation removed successfully.',
                                type: 'success',
                                title: 'Success!'
                            });
                        })
                        .catch(err => console.log('err from delete', err));
                });
            },

            showContactInfo() {
                if (this.width < 1199) {
                    this.showConversation = false;
                    this.showDetailsContact = true;
                }
            },

            backConversations() {
                this.showConversations = true;
                this.$store.commit('activeChatMessenger', false);
                this.chatActive = null;
                this.conversations.map(conversation => (conversation.active = false));
            },

            backDetailsContact() {
                this.showDetailsContact = false;
                this.showConversation = true;
            },

            backStartChat() {
                this.$store.commit('activeChatMessenger', false);
                this.startChat = false;
                this.showConversations = true;
            },

            reponsiveMode() {
                if (this.startChat && this.width > 991) {
                    this.$store.commit('activeChatMessenger', false);
                    this.showConversations = true;
                    return;
                }

                if (this.startChat && this.width < 992) {
                    this.$store.commit('activeChatMessenger', true);
                    this.showConversations = false;
                    return;
                }

                if (this.width > 1199) {
                    this.showDetailsContact = true;
                }

                if (this.width >= 992 && this.width <= 1199) {
                    this.showDetailsContact = false;
                }

                if (this.width > 991) {
                    this.$store.commit('activeChatMessenger', false);
                    this.showConversations = true;
                    this.showConversation = true;
                    if (this.conversations.length && !this.chatActive) {
                        this.loadChat(this.chatActive ? this.chatActive : this.conversations[0]);
                    }
                    return;
                }

                if (this.chatActive) {
                    this.showConversations = false;
                    this.showDetailsContact = false;

                    if (this.width < 992) {
                        this.$store.commit('activeChatMessenger', true);
                    }
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .h-screen {
        @media (min-width: 992px) {
            height: 100vh;
        }
    }

    #msgHistory {
        word-wrap: break-word;
        height: 100%;
        min-height: 400px;

        @media (max-width: 991px) {
            height: calc(100vh - 100px);
        }
    }

    .date__message {
        white-space: nowrap;
    }

    #dropdownConversation {
        outline: none;
    }

    .contact__info {
        @media (max-width: 1199px) {
            height: calc(100% - 65px);
        }
    }

    .btn-angle {
        font-weight: 900;
        font-size: 1rem;
        background: #ffffff;
        font-family: "Font Awesome 5 Free";
        border: 1px solid #dee2e6;
        color: #0374d7;
        margin-left: -1px;
        position: relative;
        display: block;
        padding: 0.5rem 0.75rem;
        border-top-right-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;
        text-decoration: none;
        box-sizing: border-box;
    }

    .btn-angle :hover {
        z-index: 2;
        color: #024b8c;
        text-decoration: none;
        background-color: #e9ecef;
        border-color: #dee2e6;
    }

    .received__from {
        font-size: 0.8rem;
        color: #6b6b6b;

        &.chatActive {
            color: #e8e8e8;
        }
    }

    .fa-facebook-messenger {
        font-size: 1.2rem;
        color: #4dabf7;
    }

    .dropdown__actions {
        top: 15px;
    }

    #messageChangeStatus {
        text-align: right;
        font-size: 0.9em;
        color: #5ed6aa;
        opacity: 0.75;
    }

    .text__line {
        animation-duration: 1.25s;
        animation-fill-mode: forwards;
        animation-iteration-count: infinite;
        animation-name: placeHolderShimmer;
        animation-timing-function: linear;
        background: #f6f6f6;
        background: linear-gradient(to right, #f6f6f6 8%, #f0f0f0 18%, #f6f6f6 33%);
        background-size: 800px 104px;
    }

    .text__line {
        height: 10px;
        width: 100%;
        margin: 4px 0;
    }

    @keyframes placeHolderShimmer {
        0% {
            background-position: -468px 0;
        }
        100% {
            background-position: 468px 0;
        }
    }


    .upload-btn-wrapper {
        position: relative;
        overflow: hidden;
        display: inline-block;

        input {
            font-size: 100px;
            position: absolute;
            left: 0;
            top: 0;
            opacity: 0;
            cursor: pointer;
        }

        .messenger-bg {
            background-color: #edf0f2;
        }
        input[type=file], /* FF, IE7+, chrome (except button) */
        input[type=file]::-webkit-file-upload-button { /* chromes and blink button */
            cursor: pointer;
        }
    }
</style>
